import { Response, apiAbortController } from '@ui/src';
import api from '../../../api/api';
import {
  TariffValidators,
  TariffItem,
  TariffFormValues,
  TariffStatus,
  TariffFeeFormValues,
  TariffItemFee,
  UpdateTariffFormValues,
  TariffAction,
} from '../types';

export const tariffsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getTariffsValidator: build.query<{ data: TariffValidators }, void>({
      query: () => ({
        url: '/v1/tariffs/validators',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTariffsList: build.query<Response<TariffItem>, string>({
      query: (query) => ({
        url: `/v1/tariffs${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getAllTariffs: build.query<Response<TariffItem>, string>({
      query: (query) => ({
        url: `/v1/tariffs?all=true${query}&status=PUBLISHED`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getTariffItem: build.query<{ data: TariffItem }, string>({
      query: (id) => ({
        url: `/v1/tariffs/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createTariff: build.mutation<
      {
        data: TariffItem;
      },
      TariffFormValues
    >({
      query: (body) => ({
        url: '/v1/tariffs',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createTariffFee: build.mutation<
      {
        data: TariffItemFee;
      },
      { body: TariffFeeFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}/fees`,
        method: 'POST',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTariffFee: build.mutation<
      {
        data: TariffItemFee;
      },
      { body: TariffFeeFormValues; id: string; feeId: string }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}/fees/${data.feeId}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    duplicateTariff: build.mutation<
      {
        data: TariffItem;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/tariffs/${id}/duplicate`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    toggleTariffActive: build.mutation<
      {
        data: TariffItem;
      },
      { body: { active: boolean }; id: string }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}/active`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    changeTariffStatus: build.mutation<
      {
        data: TariffItem;
      },
      {
        body: {
          status: TariffStatus;
          previousTariffId?: string;
          action?: TariffAction;
          nextTariffId?: string;
        };
        id: string;
      }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}/status`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    toggleTariffDefault: build.mutation<
      {
        data: TariffItem;
      },
      { body: { default: boolean }; id: string }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}/default`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    updateTariff: build.mutation<
      {
        data: TariffItem;
      },
      { body: UpdateTariffFormValues; id: string }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}`,
        method: 'PUT',
        body: data.body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteTariffFee: build.mutation<
      {
        data: TariffItemFee;
      },
      { id: string; feeId: string }
    >({
      query: (data) => ({
        url: `/v1/tariffs/${data.id}/fees/${data.feeId}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteTariff: build.mutation<
      {
        data: TariffItem;
      },
      string
    >({
      query: (id) => ({
        url: `/v1/tariffs/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useGetTariffsValidatorQuery,
  useLazyGetTariffsValidatorQuery,
  useLazyGetTariffsListQuery,
  useLazyGetTariffItemQuery,
  useCreateTariffMutation,
  useToggleTariffActiveMutation,
  useToggleTariffDefaultMutation,
  useDuplicateTariffMutation,
  useChangeTariffStatusMutation,
  useCreateTariffFeeMutation,
  useDeleteTariffFeeMutation,
  useLazyGetAllTariffsQuery,
  useUpdateTariffMutation,
  useUpdateTariffFeeMutation,
  useDeleteTariffMutation,
} = tariffsApi;

export const {
  endpoints: {
    getTariffItem,
    getTariffsList,
    getTariffsValidator,
    createTariff,
    toggleTariffActive,
    toggleTariffDefault,
    duplicateTariff,
    changeTariffStatus,
    getAllTariffs,
    createTariffFee,
    updateTariff,
    deleteTariff,
    deleteTariffFee,
    updateTariffFee,
  },
} = tariffsApi;
